import { Menu, Transition } from "@headlessui/react";
import {
  Edit,
  Eye,
  ListChecks,
  MoreHorizontal,
  Printer,
  ScanFace,
  ToggleLeft,
  ToggleRight,
  Trash,
} from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/Button";
import ConfirmModal from "../../components/ConfirmModal";
import { EmptyList } from "../../components/EmptyList";
import * as Select from "../../components/Form/Select";
import { Loading } from "../../components/Loading";
import { IEventApcap, IUser } from "../../@types/system";
import api from "../../service/api";
import { useStore } from "../../store";
import { useLoading } from "../../store/useLoading";
import { formatDate, hasPermission } from "../../utils/helpers";
import { toastError, toastSuccess } from "../../utils/toast";

const ITEMS_PER_PAGE = 10;

export function ListEventos() {
  const navigate = useNavigate();
  const user = useStore((store) => store.user);
  const loading = useLoading((store) => store.isLoading);
  const setLoading = useLoading((store) => store.setLoading);
  const events = useStore((store) => store.events);
  const setEvents = useStore((store) => store.setEvents);

  const [showSubscription, setShowSubscription] = useState(false);
  const [showPresencePicker, setShowPresencePicker] = useState(false);
  const [event, setEvent] = useState({} as IEventApcap);
  const [usersInEvent, setUsersInEvent] = useState<IUser[]>([]);
  const [userId, setUserId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const dateA = String(a.initial_date || "").slice(0, 10);
      const dateB = String(b.initial_date || "").slice(0, 10);
      // Mais recente primeiro
      return dateB.localeCompare(dateA);
    });
  }, [events]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedEvents.length / ITEMS_PER_PAGE)
  );

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedEvents.slice(start, start + ITEMS_PER_PAGE);
  }, [currentPage, sortedEvents]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  async function getData() {
    setLoading(true);
    try {
      const res = await api.get(`/events`);
      setEvents(res.data);
      setCurrentPage(1);
    } catch {
      toastError("Erro ao tentar carregar os dados, recarregue a página");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (user.id) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  async function openPresenceFlow(selected: IEventApcap) {
    setEvent(selected);
    if (user.user_type === "ADMIN" || user.user_type === "SUPERADMIN") {
      setLoading(true);
      try {
        const res = await api.get(`/events/users/${selected.id}`);
        setUsersInEvent(res.data);
        setUserId("");
        setShowPresencePicker(true);
      } catch {
        toastError("Erro ao carregar entidades do evento");
      } finally {
        setLoading(false);
      }
      return;
    }
    navigate(`/eventos/${selected.id}/presenca`);
  }

  async function updateSubscription() {
    try {
      await api.patch("events/update-subscription", {
        id: event.id,
        enable_subscription: !event.enable_subscription,
      });
      toastSuccess("Inscrição alterada com sucesso!");
      setShowSubscription(false);
      getData();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || "Erro ao alterar a inscrição";
      toastError(message);
    }
  }

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  return (
    <>
      {loading && <Loading />}
      {!loading && events.length === 0 && <EmptyList />}
      {!loading && events.length > 0 && (
        <>
          <ul
            role="list"
            className="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900"
          >
            {currentItems.map((item) => (
              <li
                key={item.id}
                className="flex flex-col justify-between gap-x-6 border-b border-zinc-200 px-4 py-5 last:border-b-0 md:flex-row dark:border-zinc-800"
              >
                <div className="mb-2 min-w-0 flex-auto lg:mb-0">
                  <p className="text-sm font-semibold leading-6 text-gray-900 dark:text-white">
                    {item.name}
                  </p>
                  <p className="mt-1 truncate text-sm leading-5 text-gray-500 dark:text-zinc-400">
                    {`Início: ${formatDate(
                      item.initial_date
                    )} | Fim: ${formatDate(
                      item.finish_date
                    )} | Inscrições abertas: ${
                      item.enable_subscription ? "Sim" : "Não"
                    }`}
                  </p>
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    title="Credenciamento"
                    className="flex items-center text-zinc-500"
                    disabled={!item.enable_subscription}
                    onClick={() =>
                      navigate(`/eventos/${item.id}/credenciamento`)
                    }
                  >
                    <ScanFace className="h-4 w-4" strokeWidth={3} />
                    <span className="ml-2 hidden lg:inline">
                      Credenciamento
                    </span>
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    title="Lista de presença"
                    className="flex items-center text-zinc-500"
                    onClick={() => openPresenceFlow(item)}
                  >
                    <ListChecks className="h-4 w-4" strokeWidth={3} />
                    <span className="ml-2 hidden lg:inline">Presença</span>
                  </Button>

                  <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-semibold text-zinc-500 shadow-sm outline-none hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-apcap/35">
                      <MoreHorizontal className="h-4 w-4" strokeWidth={3} />
                      <span className="ml-0 hidden lg:inline">Mais</span>
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-lg border border-zinc-200 bg-white py-1 shadow-lg focus:outline-none dark:border-zinc-700 dark:bg-zinc-900">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              className={`${
                                active ? "bg-zinc-50 dark:bg-zinc-800" : ""
                              } flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-zinc-700 dark:text-zinc-200`}
                              onClick={() => navigate(`/eventos/${item.id}`)}
                            >
                              <Eye className="h-4 w-4" />
                              Detalhes
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              className={`${
                                active ? "bg-zinc-50 dark:bg-zinc-800" : ""
                              } flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-zinc-700 dark:text-zinc-200`}
                              onClick={() =>
                                navigate(`/eventos/${item.id}/certificados`)
                              }
                            >
                              <Printer className="h-4 w-4" />
                              Gerar certificados
                            </button>
                          )}
                        </Menu.Item>
                        {hasPermission(user.user_type) && (
                          <>
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  type="button"
                                  className={`${
                                    active ? "bg-zinc-50 dark:bg-zinc-800" : ""
                                  } flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-zinc-700 dark:text-zinc-200`}
                                  onClick={() =>
                                    navigate(`/eventos/${item.id}/editar`)
                                  }
                                >
                                  <Edit className="h-4 w-4" />
                                  Editar
                                </button>
                              )}
                            </Menu.Item>
                            <Menu.Item>
                              {({ active }) => (
                                <button
                                  type="button"
                                  className={`${
                                    active ? "bg-zinc-50 dark:bg-zinc-800" : ""
                                  } flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-zinc-700 dark:text-zinc-200`}
                                  onClick={() => {
                                    setEvent(item);
                                    setShowSubscription(true);
                                  }}
                                >
                                  {item.enable_subscription ? (
                                    <ToggleRight className="h-4 w-4 text-emerald-600" />
                                  ) : (
                                    <ToggleLeft className="h-4 w-4 text-red-500" />
                                  )}
                                  {item.enable_subscription
                                    ? "Desabilitar inscrições"
                                    : "Habilitar inscrições"}
                                </button>
                              )}
                            </Menu.Item>
                            <Menu.Item disabled>
                              {({ active }) => (
                                <button
                                  type="button"
                                  disabled
                                  title="Exclusão de eventos indisponível"
                                  className={`${
                                    active ? "bg-zinc-50" : ""
                                  } flex w-full cursor-not-allowed items-center gap-2 px-3 py-2 text-left text-sm text-zinc-400`}
                                >
                                  <Trash className="h-4 w-4" />
                                  Excluir (indisponível)
                                </button>
                              )}
                            </Menu.Item>
                          </>
                        )}
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex flex-col gap-3 border-t border-zinc-100 px-1 py-3 sm:flex-row sm:items-center sm:justify-between dark:border-zinc-800">
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Mostrando{" "}
              <span className="font-medium text-zinc-900 dark:text-white">
                {(currentPage - 1) * ITEMS_PER_PAGE + 1}
              </span>
              –
              <span className="font-medium text-zinc-900 dark:text-white">
                {Math.min(currentPage * ITEMS_PER_PAGE, sortedEvents.length)}
              </span>{" "}
              de{" "}
              <span className="font-medium text-zinc-900 dark:text-white">
                {sortedEvents.length}
              </span>{" "}
              eventos
            </p>

            <ol className="flex justify-end gap-1 text-xs font-medium">
              <li>
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`inline-flex size-8 items-center justify-center rounded border border-zinc-200 bg-white text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 ${
                    currentPage === 1 ? "cursor-not-allowed opacity-50" : ""
                  }`}
                  aria-label="Página anterior"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </li>

              {(() => {
                const pageNumbers = [];
                let startPage = Math.max(1, currentPage - 2);
                let endPage = Math.min(totalPages, currentPage + 2);

                if (currentPage <= 3) {
                  startPage = 1;
                  endPage = Math.min(totalPages, 5);
                } else if (currentPage >= totalPages - 2) {
                  startPage = Math.max(1, totalPages - 4);
                  endPage = totalPages;
                }

                for (let i = startPage; i <= endPage; i++) {
                  pageNumbers.push(
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => handlePageChange(i)}
                        className={`block size-8 rounded border text-center leading-8 ${
                          currentPage === i
                            ? "border-apcap bg-apcap text-white"
                            : "border-zinc-200 text-zinc-900 dark:border-zinc-700 dark:text-zinc-100"
                        }`}
                      >
                        {i}
                      </button>
                    </li>
                  );
                }

                return (
                  <>
                    {startPage > 1 && (
                      <li>
                        <span className="block size-8 pt-2 text-center text-zinc-400">
                          ...
                        </span>
                      </li>
                    )}
                    {pageNumbers}
                    {endPage < totalPages && (
                      <li>
                        <span className="block size-8 pt-2 text-center text-zinc-400">
                          ...
                        </span>
                      </li>
                    )}
                  </>
                );
              })()}

              <li>
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`inline-flex size-8 items-center justify-center rounded border border-zinc-200 bg-white text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 ${
                    currentPage === totalPages
                      ? "cursor-not-allowed opacity-50"
                      : ""
                  }`}
                  aria-label="Próxima página"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </li>
            </ol>
          </div>
        </>
      )}

      <ConfirmModal
        open={showSubscription}
        onClose={() => setShowSubscription(false)}
        onConfirm={updateSubscription}
        title={event.name || "Inscrições"}
        description={
          event.enable_subscription
            ? "Deseja desabilitar as inscrições deste evento?"
            : "Deseja habilitar as inscrições deste evento?"
        }
        confirmLabel={
          event.enable_subscription ? "Desabilitar" : "Habilitar"
        }
        variant={event.enable_subscription ? "danger" : "confirm"}
      />

      <ConfirmModal
        open={showPresencePicker}
        onClose={() => setShowPresencePicker(false)}
        onConfirm={() => {
          if (!userId) {
            toastError("Selecione uma entidade");
            return;
          }
          setShowPresencePicker(false);
          navigate(`/eventos/${event.id}/presenca?client=${userId}`);
        }}
        title={event.name || "Lista de presença"}
        description="Selecione a entidade para visualizar as presenças."
        confirmLabel="Abrir lista"
        confirmDisabled={!userId}
      >
        <Select.Root name="userId" value={userId} onValueChange={setUserId}>
          <Select.Trigger>
            <Select.Value placeholder="Selecione o cliente" />
          </Select.Trigger>
          <Select.Content>
            {usersInEvent.map((entity) => (
              <Select.Item value={entity.id} key={entity.id}>
                <Select.ItemText>{entity.name}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </ConfirmModal>
    </>
  );
}
